// Shared GraphQL query builders and product mapper for initial sync + re-sync.
// Both Sync/index.js (first-time) and syncServices.js (incremental) import from here
// so field lists stay in sync and bugs like missing alternateUom / pricing rule fields
// cannot recur.

// ─── UOM Query ────────────────────────────────────────────────────────────────

export function buildUomQuery(syncParams) {
  const params = syncParams
    ? `lastSyncTime: ${syncParams.lastSyncTime ? `"${syncParams.lastSyncTime}"` : null}, uniqueId: "${syncParams.uniqueId}", tillId:"${syncParams.tillId}", storeOpsTillId: null, timeZone:"${syncParams.timeZone}"`
    : "";
  return {
    query: `query {
      getUom${params ? `(${params})` : ""} {
        csUomId
        costingprecision
        description
        ediCode
        name
        stdprecision
        symbol
        decimal
      }
    }`,
  };
}

// ─── Product Category Query ──────────────────────────────────────────────────

export function buildProductCategoryQuery(tillId, syncParams) {
  const extraParams = syncParams
    ? `, lastSyncTime: ${syncParams.lastSyncTime ? `"${syncParams.lastSyncTime}"` : null}, timeZone:"${syncParams.timeZone}"`
    : "";
  return {
    query: `query {
      getProductCategory(tillId:"${tillId}"${extraParams}) {
        mProductCategoryId
        value
        name
        description
        imageurl
      }
    }`,
  };
}

// ─── POS Sale Types Query ───────────────────────────────────────────────────

export function buildPosSaleTypesQuery(catalogueId, syncParams) {
  const params = syncParams
    ? `tillId:"${syncParams.tillId}", catalogueId:"${catalogueId}", lastSyncTime: ${syncParams.lastSyncTime ? `"${syncParams.lastSyncTime}"` : null}, uniqueId: "${syncParams.uniqueId}", storeOpsTillId: null, timeZone:"${syncParams.timeZone}"`
    : `catalogueId:"${catalogueId}"`;
  return {
    query: `query {
      getPosSaletype(${params}) {
        cwrPcatalogueSaletypeId
        cSClientID
        cSBunitID
        created
        createdby
        updated
        updatedby
        isactive
        isPromoApplicable
        cwrPCatalogueId
        cwrSaletype {
          isAggregator
          cwrSaletypeId
          value
          name
          isdefault
          fulfilmentType
          colourCode
          enableLayaway
          enableKot
        }
        customer {
          b2cCustomerId
          code
          name
          email
          mobileNo
          pincode
          retlLoyaltyBalance
          b2cRegisteredstoreId
          iscredit
          balancePoints
          sPriceListId
          priceListName
          cwrCustomerId
          sCustomerId
        }
        additionalCharges {
          chargeType
          defaultPercentage
          defaultAmount
          minValue
          maxValue
          isEditableInPos
          isTaxable
          productId
          taxId
          conditionsFormula
          additionalChargesId
        }
      }
    }`,
  };
}

// ─── Products Query ──────────────────────────────────────────────────────────

export function buildProductsQuery(bUnitId, from, limit, syncParams, options = {}) {
  const extraParams = syncParams
    ? `, lastSyncTime: ${syncParams.lastSyncTime ? `"${syncParams.lastSyncTime}"` : null}, tillId:"${syncParams.tillId}", uniqueId: "${syncParams.uniqueId}", storeOpsTillId: null, timeZone:"${syncParams.timeZone}"`
    : "";
  const valueParam = options.value ? `, value:"${options.value}"` : "";
  return {
    query: `query {
      getProducts1(bUnit:"${bUnitId}"${extraParams}${valueParam}, from:${from} limit:${limit}) {
        mProductId
        csClientId
        csBunitId
        cwrPcatalogueId
        productAliasName
        created
        createdby
        updated
        isactive
        updatedby
        sunitprice
        slistprice
        onhandQty
        description
        isVirtual
        isBestSeller
        cTaxId
        taxRate
        taxName
        interTaxId
        interTaxRate
        interTaxName
        isPromoApplicable
        isavailable
        ismonday
        istuesday
        iswednesday
        isthursday
        isfriday
        issaturday
        issunday
        colorcode
        productionCenter
        productionCenterId
        printerId
        preparationTime
        cwrMenuTimeslot {
          cwrMenuTimeslotId
          name
          startTime
          endTime
        }
        mProduct {
          csBunitId
          companion
          bunitName
          imageUrl
          value
          name
          name2
          type
          showAlternateUomInPos
          csTaxcategoryId
          mProductCategoryId
          csUomId
          uomName
          upc
          batchedProduct
          isManualQty
          restrictPosQtyEdit
          isDecimal
          imageurl
          shortDescription
          hsncode
          returnable
          returnDays
          description
          batchedForSale
          batchedForStock
          multiPrice
          addNewLine
          shelfLife
          posPriceEdit
          isBag
          showAddons
          foodType
          productSegment
          iscustomizable
          taxCategory {
            csTaxcategoryID
            name
            overRideTax
            overRideCondition
            contraTaxCategoryId
            contraTaxCategory {
              contraTaxCategoryName
              contraTaxId
              contraTaxName
              contraRate
            }
          }
          alternateUom {
            name
            ediCode
            csUomId
            convertionRate
          }
          mBatch {
            mBatchId
            batchno
            upc
            price
            listPrice
            startdate
            enddate
            life
          }
          productGroup {
            mProductGroupId
            name
            value
            description
          }
          productAddons {
            mProductAddOnId
            name
            price
            mAddonGroup {
              mAddonGroupId
              name
              maxqty
              minqty
              type
              allowQtySelection
            }
            mAddonProduct {
              mProductId
              name
            }
          }
          itemType
          productAttributes {
            mProductAttributeId
            value
            mAttributeGroup {
              mAttributeGroupId
              name
            }
            mAttribute {
              mAttributeId
              name
            }
          }
          productManufacturer {
            mProductManufacturerId
            name
            value
            description
          }
          productBrand {
            brandId
            name
            value
            description
          }
          priceLists {
            pricelist
            pricestd
            sPricelistId
            sPriceList
            uomId
            uomName
            effectiveFrom
          }
          customAttributes {
            name
            fileds {
              name
              type
              values
              defaultValue
              mandatory
            }
          }
        }
      }
    }`,
  };
}

// ─── Pricing Rules Query ─────────────────────────────────────────────────────

export function buildPricingRulesQuery(catalogueId, syncParams) {
  const extraParams = syncParams
    ? `, lastSyncTime: ${syncParams.lastSyncTime ? `"${syncParams.lastSyncTime}"` : null}, tillId:"${syncParams.tillId}", uniqueId: "${syncParams.uniqueId}", storeOpsTillId: null, timeZone:"${syncParams.timeZone}"`
    : "";
  return {
    query: `query {
      getPricingRules(catalogueId: "${catalogueId}"${extraParams}) {
        mPricingrulesId
        externalCoupon
        couponProviderId
        csClientId
        csBunitId
        created
        createdBy
        updated
        updatedBy
        upc
        type
        discountType
        name
        printedName
        description
        startDate
        endDate
        nextRule
        percentageDiscount
        amountDiscount
        minimumQty
        maximumQty
        xQty
        yQty
        billAmount
        maxBillAmount
        cwrSaletypeId
        fixedUnitPrice
        timeSpecific
        starttime
        endtime
        monday
        tuesday
        wednesday
        thursday
        friday
        saturday
        sunday
        status
        iscoupon
        priority
        giftVoucherType
        issueGiftVoucher
        manualDiscount
        excludeProductCategories
        excludeBusinessUnits
        excludeProducts
        excludeB2CCustomers
        excludeBrands
        excludeCustomers
        excludeCustomerCategory
        excludeB2CSegment
        enableApproval
        commTemplateId
        mPricingXProducts {
          mPricingXProductId
          line
          mProductId
          mBatchId
          isFree
          quantity
        }
        mPricingYProducts {
          mPricingYProductId
          line
          mProductId
        }
        mPricingCcategories {
          mPricingCcategoryId
          line
          sCustomerCategoryId
        }
        mPricingPcategories {
          mPricingPcategoryId
          line
          mProductCategoryId
        }
        mPricingBrands {
          mPricingBrandId
          line
          mBrandId
        }
        mPricingBUnits {
          mPricingBUnitId
          line
          mBunitPricingId
        }
        mPricingQuantities {
          mPricingQuantityId
          line
          quantity
          discountValue
        }
        mPricingB2CCustomers {
          mPricingB2cCustomerId
          lineno
          b2cCustomerId
        }
        mPricingB2CCustomerSegments {
          mPricingB2CSegmentId
          lineNo
          cwrB2CCustomerSegmentId
        }
        mPricingExpiryDiscount {
          rangeFrom
          rangeTo
          discountPercentage
          mProductId
        }
        mPricingApprovals {
          mPricingApprovalId
          type
          min
          max
          userId
          roleId
          userName
          pin
          role
          enableOTP
        }
        additionalInfo {
          fieldName
          fieldType
          mandatory
          sequence
          displayLabel
          validationRule
        }
        couponProvider {
          providerCode
          providerName
          apiConfig
        }
      }
    }`,
  };
}

// ─── Pricing Policy Query ───────────────────────────────────────────────────

export function buildPricingPolicyQuery(tillId) {
  return {
    query: `query { getPricingPolicy(tillId: "${tillId}") }`,
  };
}

// ─── Pricing Policy Mapper ──────────────────────────────────────────────────
// Flattens nested API response (policies with scopes[]) into one DB row per scope.

export function mapPricingPolicyToDb(policies) {
  if (!Array.isArray(policies)) return [];
  const rows = [];
  for (const policy of policies) {
    if (!Array.isArray(policy.scopes)) continue;
    for (const scope of policy.scopes) {
      // Derive type from whichever FK is populated — avoids relying on backend dim codes
      const dimensionCode = scope.mProductId ? "PRD" : scope.mBrandId ? "BRD" : scope.mProductCategoryId ? "CAT" : null;
      const dimensionValue = scope.mProductId || scope.mBrandId || scope.mProductCategoryId || null;
      if (!dimensionCode || !dimensionValue) continue;
      rows.push({
        dimensionCode,
        dimensionValue,
        priceSource: policy.priceSource,
        pricingPolicyId: policy.pricingPolicyId,
      });
    }
  }
  return rows;
}

// ─── Product Data Mapper ─────────────────────────────────────────────────────
// Maps a GraphQL getProducts1 response item into the IndexedDB product shape.
// Used by initial sync, incremental sync, and single-product sync.

export function mapProductToDb(product, uomData, options = {}) {
  const p = product;
  const m = p.mProduct;

  if (m?.value === "03.9300") {
  }

  const upcIndex = [];
  const batchIndex = [];
  if (m.mBatch != null) {
    for (let j = 0; j < m.mBatch.length; j += 1) {
      batchIndex.push(m.mBatch[j].batchno);
      upcIndex.push(m.mBatch[j].upc);
    }
  }
  if (m.upc != null) {
    upcIndex.push(m.upc);
  }

  const obj = {
    mProductId: p.mProductId || "",
    csClientId: p.csClientId || "",
    csBunitId: m.csBunitId || p.csBunitId || "",
    cwrPcatalogueId: p.cwrPcatalogueId || "",
    productAliasName: p.productAliasName || "",
    created: p.created || "",
    createdby: p.createdby || "",
    updated: p.updated || "",
    isactive: p.isactive || "",
    productionCenter: p.productionCenter || "",
    productionCenterId: p.productionCenterId || "",
    preparationTime: p.preparationTime || "",
    printerId: p.printerId || "",
    updatedby: p.updatedby || "",
    sunitprice: p.sunitprice || 0,
    addNewLine: m.addNewLine || "N",
    slistprice: p.slistprice || 0,
    onhandQty: p.onhandQty || 0,
    description: p.description || "",
    shortDescription: m.shortDescription || "",
    isVirtual: p.isVirtual || "",
    isBestSeller: p.isBestSeller || "",
    cTaxId: p.cTaxId || "",
    taxRate: p.taxRate || 0,
    interTaxId: p.interTaxId || "",
    interTaxRate: p.interTaxRate || 0,
    interTaxName: p.interTaxName || "",
    isPromoApplicable: p.isPromoApplicable || "",
    value: m.value || "",
    showAddons: m.showAddons || "N",
    foodType: m.foodType || "",
    itemType: m.itemType || "",
    name: m.name || "",
    name2: m.name2 || "",
    brandId: m.productBrand?.brandId || "",
    csTaxcategoryId: m.csTaxcategoryId || "",
    mProductCategoryId: m.mProductCategoryId || "",
    csUomId: m.csUomId || "",
    uomName: m.uomName || "",
    uomData: uomData || [],
    upc: m.upc || "",
    batchedProduct: m.batchedProduct || "",
    batchedForSale: m.batchedForSale || "",
    batchedForStock: m.batchedForStock || "",
    bunitName: m.bunitName || "",
    imageUrl: m.imageUrl || "",
    isManualQty: m.isManualQty || "",
    restrictPosQtyEdit: m.restrictPosQtyEdit || "N",
    showAlternateUomInPos: m.showAlternateUomInPos || "N",
    alternateUom: m.alternateUom || null,
    companion: m.companion || "N",
    isDecimal: m.isDecimal || "",
    imageurl: m.imageurl || "",
    productSegment: m.productSegment || "",
    taxCategory: m.taxCategory?.name || "",
    taxName: p.taxName || "",
    taxFlag: "N",
    mBatch: m.mBatch || "",
    hsncode: m.hsncode || "",
    batchIndex: batchIndex,
    upcIndex: upcIndex,
    customAttributes: m.customAttributes?.fileds || [],
    cwrMenuTimeslot: p.cwrMenuTimeslot || [],
    type: m.type || "",
    priceList: m.priceLists,
    productManufacturerName: m.productManufacturer?.name || "",
    productManufacturerId: m.productManufacturer?.mProductManufacturerId || "",
    productBrandName: m.productBrand?.name || "",
    productBrandId: m.productBrand?.brandId || "",
    iscustomizable: m.iscustomizable || "",
    mProductGroupId: m.productGroup?.mProductGroupId || "",
    productCategoryName: m.productGroup?.name || "",
    productAddons: m.productAddons || [],
    multiPrice: m.multiPrice || "",
    shelfLife: m.shelfLife || "",
    posPriceEdit: m.posPriceEdit || "",
    overRideTax: m.taxCategory?.overRideTax || "",
    overRideCondition: parseFloat(m.taxCategory?.overRideCondition) || "",
    contraRate: parseFloat(m.taxCategory?.contraTaxCategory?.contraRate) || "",
    contraTaxId: m.taxCategory?.contraTaxCategory?.contraTaxId || "",
  };

  if (options.syncId) obj.syncId = options.syncId;

  return obj;
}
